import { useState } from 'react'
import { Loader2, PencilLine, Plus, Search, Store, X } from 'lucide-react'
import { useProducts } from '@/api/hooks'
import type { components } from '@/api/schema'
import { Button } from '@/components/ui/button'
import { LoadingButton } from '@/components/loading-button'
import { InlineFieldInput } from '@/components/inline-field-group'
import { SearchInput } from '@/components/search-input'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { cn, formatMoney } from '@/lib/utils'

export type CatalogLineProduct = components['schemas']['DashboardProductListRead']

function thumbnail(product: CatalogLineProduct) {
  const image = product.images?.find((candidate) => !candidate.hidden && (candidate.public_url || candidate.store_url))
  return image ? (image.public_url ?? image.store_url ?? null) : null
}

/** The product's name, led by its manufacturer unless the name already starts with it. */
function productLine(product: CatalogLineProduct) {
  const title = product.store_title ?? product.specifications ?? ''
  const maker = product.manufacturer_name ?? ''
  if (!maker || title.toLowerCase().startsWith(maker.toLowerCase())) return title
  return [maker, title].filter(Boolean).join(' · ')
}

function productNet(product: CatalogLineProduct) {
  const price = product.current_price
  return price?.base_net_price ?? price?.net_price ?? null
}

/**
 * Adding a product to an order starts with the catalog (USA-1580): search by model, name or spec,
 * scoped to the order's manufacturer until staff widen it, pick a row, set the quantity, add. The
 * typed-out form is behind "Add manual item" for what the catalog doesn't carry.
 */
export function CatalogLineSearch({
  manufacturer,
  pending = false,
  onAdd,
  onManual,
}: {
  /** Search this manufacturer's products first; staff can clear it to search everything. */
  manufacturer?: string | null
  pending?: boolean
  onAdd: (product: CatalogLineProduct, quantity: number) => void
  onManual: () => void
}) {
  const [query, setQuery] = useState('')
  const [scoped, setScoped] = useState(Boolean(manufacturer))
  const [selected, setSelected] = useState<CatalogLineProduct | null>(null)
  const [quantity, setQuantity] = useState('1')
  const debounced = useDebouncedValue(query.trim(), 250)
  const scope = scoped && manufacturer ? manufacturer : undefined
  const searching = Boolean(debounced || scope)
  const products = useProducts({
    page: 1,
    limit: 8,
    search: debounced || undefined,
    manufacturer: scope,
    discontinued: false,
  })
  const items = searching ? (products.data?.items ?? []) : []
  const quantityNum = Number(quantity)
  const quantityValid = Number.isInteger(quantityNum) && quantityNum >= 1

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div className="relative">
        <Search
          aria-hidden
          className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
        />
        <SearchInput
          autoFocus
          value={query}
          onChange={(event) => {
            setQuery(event.target.value)
            setSelected(null)
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') event.preventDefault()
          }}
          placeholder={scope ? `Search ${scope}` : 'Search the catalog'}
          aria-label="Search the catalog"
          className="h-9 pl-8 text-base pointer-fine:h-8 pointer-fine:text-sm"
        />
      </div>
      {scope && (
        <div className="flex items-center gap-1.5 text-xs">
          <span className="text-muted-foreground">In</span>
          <span className="inline-flex min-w-0 items-center gap-1 rounded-full bg-muted py-0.5 pl-2 pr-0.5 font-medium">
            <span className="truncate">{scope}</span>
            <button
              type="button"
              aria-label={`Search beyond ${scope}`}
              onClick={() => {
                setScoped(false)
                setSelected(null)
              }}
              className="rounded-full p-0.5 text-muted-foreground hover:bg-background hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
            >
              <X className="size-3" />
            </button>
          </span>
        </div>
      )}

      <div className="-mx-1 max-h-[min(24rem,55dvh)] overflow-y-auto px-1">
        {!searching ? (
          <p className="px-1 py-3 text-sm text-muted-foreground">Type a model, name or spec.</p>
        ) : products.isLoading ? (
          <p className="flex items-center gap-2 px-1 py-3 text-sm text-muted-foreground">
            <Loader2 className="size-3.5 animate-spin" aria-hidden /> Searching…
          </p>
        ) : products.isError ? (
          <p className="px-1 py-3 text-sm text-destructive">Product search failed. Try again.</p>
        ) : items.length === 0 ? (
          <p className="px-1 py-3 text-sm text-muted-foreground">
            No products match{scope ? ` in ${scope}` : ''}.
          </p>
        ) : (
          <ul className="space-y-0.5" aria-label="Catalog products">
            {items.map((product) => {
              const src = thumbnail(product)
              const net = productNet(product)
              const isSelected = selected?.uuid === product.uuid
              return (
                <li key={product.uuid}>
                  <button
                    type="button"
                    aria-pressed={isSelected}
                    onClick={() => {
                      setSelected(isSelected ? null : product)
                      setQuantity('1')
                    }}
                    className={cn(
                      'flex w-full min-w-0 items-center gap-2.5 rounded-md px-1.5 py-1.5 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
                      isSelected && 'bg-accent',
                    )}
                  >
                    {src ? (
                      <img src={src} alt="" loading="lazy" className="size-10 shrink-0 rounded bg-white object-contain" />
                    ) : (
                      <span aria-hidden className="flex size-10 shrink-0 items-center justify-center rounded bg-muted text-muted-foreground">
                        <Store className="size-4" />
                      </span>
                    )}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{product.model}</span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {productLine(product)}
                      </span>
                    </span>
                    <span className="shrink-0 text-right text-xs tabular-nums">
                      {net != null ? (
                        <>
                          <span className="block font-medium">{formatMoney(net)}</span>
                          <span className="block text-muted-foreground">Net</span>
                        </>
                      ) : (
                        <span className="text-muted-foreground">No Net</span>
                      )}
                    </span>
                  </button>
                  {isSelected && (
                    <form
                      className="flex items-center justify-end gap-2 px-1.5 pb-2 pt-1"
                      onSubmit={(event) => {
                        event.preventDefault()
                        if (quantityValid && !pending) onAdd(product, quantityNum)
                      }}
                    >
                      <label htmlFor={`catalog-qty-${product.uuid}`} className="text-xs text-muted-foreground">
                        Quantity
                      </label>
                      <InlineFieldInput
                        id={`catalog-qty-${product.uuid}`}
                        autoFocus
                        inputMode="numeric"
                        value={quantity}
                        aria-invalid={!quantityValid || undefined}
                        onChange={(event) => setQuantity(event.target.value)}
                        className="w-16 text-right tabular-nums"
                      />
                      <LoadingButton
                        type="submit"
                        size="sm"
                        loading={pending}
                        loadingText="Adding..."
                        disabled={!quantityValid}
                      >
                        <Plus className="size-3.5" /> Add line item
                      </LoadingButton>
                    </form>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </div>

      <div className="border-t pt-2">
        <Button type="button" variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={onManual}>
          <PencilLine className="size-3.5" /> Add manual item
        </Button>
      </div>
    </div>
  )
}
